import type { AnalyticsType } from '@ria-client/segment/types';
import { Vertical } from '@ria-client/supergraph/marketplace/constants';
import type { CtaType } from '@ria-client/supergraph/roundup/types';
import type { StructuredContentDocument } from '@ria-client/supergraph/structuredContent/types';
import { Details } from '@ria-client/tw-shared-ui';
import { ChevronDown } from 'lucide-react';
import { StructuredContentRenderer } from '../../renderer';

export interface OurTakeProps {
  bottomLine?: StructuredContentDocument;
  cons?: StructuredContentDocument[];
  cta?: CtaType;
  ctaAnalytics?: AnalyticsType;
  isDefaultExpanded?: boolean;
  productType?: string;
  pros?: StructuredContentDocument[];
  structuredContentTagline?: StructuredContentDocument;
}

const isValidProductType = (productType?: string): productType is Vertical =>
  productType === Vertical.Banking ||
  productType === Vertical.Investing ||
  productType === Vertical.Retirement;

export function OurTake({
  bottomLine,
  cons,
  cta,
  ctaAnalytics,
  isDefaultExpanded = false,
  productType,
  pros,
  structuredContentTagline,
}: OurTakeProps) {
  if (!isValidProductType(productType)) return null;
  if (!bottomLine && !structuredContentTagline) return null;

  return (
    <Details
      SummaryComponent={
        <>
          <span className="typography-text-small md:pl-3">Our Take</span>
          <ChevronDown aria-hidden className="size-5 opacity-55" />
        </>
      }
      className="border-t border-border"
      detailsProps={{ open: isDefaultExpanded }}
      summaryComponentProps={{
        'data-testid': 'accordion-summary',
        className: 'justify-between px-3 py-3 hover:bg-secondary md:px-4',
      }}
      variant="simple"
    >
      <div className="p-3" data-testid="accordion-details">
        <ProsAndCons cons={cons} pros={pros} />
        <div className="max-w-[705px] px-0 pb-2 break-words md:px-3">
          <p className="typography-text-small font-medium">Why We Like It</p>
          {bottomLine ? (
            <StructuredContentRenderer
              doc={bottomLine}
              productCardBottomLineCtaData={cta && ctaAnalytics ? { cta, ctaAnalytics } : undefined}
            />
          ) : (
            structuredContentTagline && <StructuredContentRenderer doc={structuredContentTagline} />
          )}
        </div>
      </div>
    </Details>
  );
}

interface ProsAndConsProps {
  cons?: StructuredContentDocument[];
  pros?: StructuredContentDocument[];
}

function ProsAndCons({ cons, pros }: ProsAndConsProps) {
  if (!pros && !cons) return null;

  return (
    <div className="max-w-[705px] px-0 pb-2 md:px-3">
      {pros && <ContentList heading="Pros" items={pros} />}
      {cons && <ContentList heading="Cons" items={cons} />}
    </div>
  );
}

interface ContentListProps {
  heading: string;
  items: StructuredContentDocument[];
}

function ContentList({ heading, items }: ContentListProps) {
  // 13px: legacy ProductCard/OurTake/ProsAndCons.tsx uses fontSize: 13.
  return (
    <div className="mb-4">
      <p className="typography-text-small font-medium">{heading}</p>
      <ul className="ml-3 list-disc py-0">
        {items.map((item, index) => (
          <li className="py-1 text-[13px]" key={`${heading}-${index}`}>
            <StructuredContentRenderer doc={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}
